import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import { useTranslation } from 'react-i18next';
import { LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import { CREDIT_CARDS_TRANSACTION_TYPES, STYLES, TRANSACTION_TYPES } from '../../consts';
import Heading from '../../../../generic-components/Heading';
import { useStore } from '../../../../store';
import { ETypesFilter } from '../../hooks/useTransactionsData';
import { FlowContext } from '../TransactionsWrapper';
import { CloseButton, CloseButtonColor, CloseButtonSize } from '../../../../DesignSystem/LiliCloseButton/CloseButton';

interface IProps {
  onCloseFilter: () => void;
  isCreditCardFilter?: boolean;
}
interface FilterTypeButtonProps {
  filter: { title: string; id: ETypesFilter };
  setLocalType: React.Dispatch<React.SetStateAction<ETypesFilter>>;
  localType: ETypesFilter;
}

const FilterTypeButton = (props: FilterTypeButtonProps) => {
  const { filter, setLocalType, localType } = props;
  return (
    <button
      type='button'
      id={filter.id}
      onClick={() => setLocalType(filter.id)}
      key={filter.id}
      className={`${STYLES.FLOAT_SETTINGS.LI} ${localType === filter.id ? 'border-action03 text-action03' : 'border-bw05 text-bw07'}`}
    >
      {filter.title}
    </button>
  );
};

export const FilterTypeAndAmountWrapper = ({ onCloseFilter, isCreditCardFilter }: IProps) => {
  const filterObject = useStore((state) => state.filterObject);
  const setFilterObject = useStore((state) => state.setFilterObject);
  const [localType, setLocalType] = useState<ETypesFilter>(filterObject?.transactionFilterType || ETypesFilter.NONE);
  const [amountFrom, setAmountFrom] = useState<number>(filterObject.amountFrom || 0);
  const [amountTo, setAmountTo] = useState<number>(filterObject.amountTo || 0);
  const elFilter = useRef<HTMLDivElement>(null);
  const ctx = useContext(FlowContext);
  const { t } = useTranslation();

  // const isSomeFieldDirty = useMemo(() => !!(amountTo || amountFrom || localType !== filterObject.transactionFilterType), [amountTo, amountFrom, localType]);

  const isAmountsValid = useMemo(() => {
    if (amountTo || amountFrom) {
      return amountTo >= amountFrom;
    }
    return true;
  }, [amountTo, amountFrom]);

  const handleClear = () => {
    setLocalType(ETypesFilter.NONE);
    setAmountFrom(0);
    setAmountTo(0);
  };

  const handleApply = () => {
    setFilterObject({
      ...filterObject,
      transactionFilterType: localType,
      amountFrom,
      amountTo,
    });
    ctx.handleFilterTypeAndAmount(localType, { from: amountFrom, to: amountTo });
    onCloseFilter();
  };

  useEffect(() => {
    window.addEventListener('click', (e) => {
      const elTriggerWrapper = document.getElementById('typeAndAmountTriggerWrapper');
      // @ts-ignore
      if (elFilter.current && !elFilter?.current?.contains(e.target) && !elTriggerWrapper?.contains(e.target)) {
        onCloseFilter();
      }
    });
    return () => {
      window.removeEventListener('click', () => {});
    };
  }, []);

  return (
    <div ref={elFilter} className={STYLES.FLOAT_SETTINGS.WRAPPER}>
      <CloseButton
        onClick={onCloseFilter}
        id='close-button'
        className={STYLES.FLOAT_DATE_PICKER.CLOSE_BTN}
        size={CloseButtonSize.Medium}
        color={CloseButtonColor.Gray}
      />
      <div className={STYLES.FLOAT_SETTINGS.CONTENT_CONTAINER}>
        {!isCreditCardFilter && (
          <div className={STYLES.FLOAT_SETTINGS.TYPE_CONTAINER}>
            <Heading
              title={t('TRANSACTIONS_FACELIFT.TYPE_AMOUNT_WRAPPER.TYPE_HEADING')}
              className={STYLES.FLOAT_SETTINGS.HEADER}
            />
            <ul className={STYLES.FLOAT_SETTINGS.UL}>
              {isCreditCardFilter
                ? CREDIT_CARDS_TRANSACTION_TYPES.map((filter) => (
                  <FilterTypeButton
                    filter={filter}
                    setLocalType={setLocalType}
                    localType={localType}
                    key={filter.id}
                  />
                ))
                : TRANSACTION_TYPES.map((filter) => (
                  <FilterTypeButton
                    filter={filter}
                    setLocalType={setLocalType}
                    localType={localType}
                    key={filter.id}
                  />
                ))}
            </ul>
          </div>
        )}

        <div className={STYLES.FLOAT_SETTINGS.AMOUNT_RANGE_CONTAINER}>
          <Heading
            title={t('TRANSACTIONS_FACELIFT.TYPE_AMOUNT_WRAPPER.AMOUNT_HEADING')}
            className={STYLES.FLOAT_SETTINGS.HEADER}
          />
          <div className='flex items-center gap-[8px]'>
            <CurrencyInput
              id='amountFrom'
              name='input-amount'
              maxLength={15}
              className={STYLES.FLOAT_SETTINGS.CURRENCY_INPUT}
              placeholder='$0.00'
              value={amountFrom}
              decimalsLimit={2}
              prefix='$'
              onValueChange={(value) => setAmountFrom(Number(value || '0'))}
            />
            <Heading
              title={t('TRANSACTIONS_FACELIFT.DATE_PICKER_WRAPPER.TO')}
              className={STYLES.FLOAT_SETTINGS.SEPARATE_HEADER}
            />
            <CurrencyInput
              id='amountTo'
              name='input-amount'
              maxLength={15}
              className={STYLES.FLOAT_SETTINGS.CURRENCY_INPUT}
              placeholder='$0.00'
              value={amountTo}
              decimalsLimit={2}
              prefix='$'
              onValueChange={(value) => setAmountTo(Number(value || '0'))}
            />
          </div>
        </div>
        <div className='w-full flex justify-end gap-[14px]'>
          <LiliButton
            onClick={() => handleClear()}
            title={t('TRANSACTIONS_FACELIFT.DATE_PICKER_WRAPPER.CLEAR')}
            className={STYLES.CLEAR_BUTTON}
            id='clear'
            type={LiliButtonType.SECONDARY}
          />
          <LiliButton
            disabled={!isAmountsValid}
            onClick={() => handleApply()}
            title={t('TRANSACTIONS_FACELIFT.DATE_PICKER_WRAPPER.APPLY')}
            className={STYLES.APPLY_BUTTON}
            id='apply'
          />
        </div>
      </div>
    </div>
  );
};
